/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type { GeoData } from '../@types';
import https from 'https';
import getConfig from '../config';
import SearchConfig from '../utils/SearchConfig';
import { getErrorMessage } from '../utils/getErrorMessage';
import { getLogger } from '../utils/SearchLogger';
class GeoService {
	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/**
	 * Retrieves the location data from Geo Service.
	 */
	async fetchGeoData(): Promise<GeoData> {
		// try to initialize with cached data first
		let geoData: GeoData = GeoService.cachedGeoData;

		if (this.isGeoDataEnabled && !geoData) {
			try {
				geoData = await this._performGeoRequest();
			} catch (e) {
				getLogger().error('GeoService', `fetchGeoData() failed, ${getErrorMessage(e)}`);
				geoData = this.geoData;
			}
		} else {
			geoData = this.geoData;
		}

		return geoData;
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	static instance: GeoService;
	readonly geoData: GeoData;
	isGeoDataEnabled: boolean;
	readonly env: string;
	static cachedGeoData: GeoData;

	constructor(env: string) {
		if (!GeoService.instance) {
			this.env = env;
			const searchConfig = SearchConfig.getSearchConfig();
			this.isGeoDataEnabled = searchConfig?.ui?.features?.fetchGeoData;
			// give it a default value
			this.geoData = {
				country: '',
				state: '',
				'Accept-Language': '',
			};
			GeoService.instance = this;
		}
		return GeoService.instance;
	}

	enableGeoData(isEnabled: boolean) {
		this.isGeoDataEnabled = isEnabled;
	}
	/**
	 * Retrieves geoData from GeoService.
	 * @returns {GeoData}
	 */
	_performGeoRequest(): Promise<GeoData> {
		return new Promise((resolve, reject) => {
			const request = https.request(
				{
					// @ts-ignore
					'Content-Type': 'application/json',
					Accept: 'application/json',
					hostname: getConfig(this.env, 'ADOBE_DOT_COM_GEO_URL'),
					method: 'GET',
				},
				(res: any) => {
					if (res.statusCode >= 300) {
						// On error, it should show the error status code
						const geoServiceError = new Error(`_performGeoRequest failed, statusCode=${res.statusCode}`);
						reject(geoServiceError);
					} else {
						let data = '';
						res.setEncoding('utf8');
						res.on('data', (chunk) => (data += chunk));
						res.on('end', () => {
							// Success!
							try {
								/* reponse looks like this:
              	{
              	"country": "US",
              	"state": "CA",
              	"Accept-Language": "en-us"
              }
              	*/
								const geo = JSON.parse(data);

								if (geo.country && geo['Accept-Language']) {
									this.geoData.country = geo.country;
									this.geoData['Accept-Language'] = geo['Accept-Language'];

									// see if there is state information
									if (geo.state) {
										this.geoData.state = geo.state;
									}

									// cache this return to a static property
									GeoService.cachedGeoData = this.geoData;
								}

								// if the response doesn't have the right data, we'll end up
								// returning the default data that the constructor provides
								resolve(this.geoData);
							} catch (err) {
								reject(err);
							}
						});
					}
				},
			);
			request.on('error', (err) => {
				const geoError = new Error(`GeoService connection error: ${getErrorMessage(err)}`);
				reject(geoError);
			});
			request.end();
		});
	}
}

export default GeoService;
